/*
 * <review-testimonial-carousel> — behaviour for sections/review-carousel.liquid.
 *
 * Not related to, and never loaded together with, the unrelated <review-carousel> custom
 * element used by the product page's star-rating reviews (assets/review-carousel.js,
 * snippets/review-carousel.liquid) — deliberately named apart so the two can never collide.
 *
 * Same offset-based positioning idea as <highlights-carousel> (assets/highlights-carousel.js,
 * untouched by this file): each slide gets a --o custom property (its distance from the active
 * slide) and CSS transitions handle the actual movement/fade — no per-frame JS loop. Swiping
 * uses pointer events (vertical scrolling is left to the browser). Autoplay is a single timer
 * that pauses while the tab is hidden, the section is off-screen, the visitor is dragging, or a
 * block is being edited in the Theme Editor — and never starts at all for prefers-reduced-motion.
 */
(function () {
  if (window.customElements.get('review-testimonial-carousel')) return;

  var clamp = function (v, a, b) {
    return Math.min(b, Math.max(a, v));
  };

  class ReviewTestimonialCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.stage = this.querySelector('[data-stage]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.n = this.slides.length;
      if (!this.stage || this.n === 0) return;
      this._ready = true;

      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
      this.intro = this.querySelector('.rc__intro');
      this.body = this.querySelector('.rc__body');

      this.cur = 0;
      this.prevO = this.slides.map(function (_, i) {
        return i;
      });
      this.loop = this.n >= 3;
      this.mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayOn = this.dataset.autoplay === 'true' && !this.mqReduce.matches && this.n > 1;
      this.hold = { hidden: document.hidden, offscreen: true, dragging: false };
      this.drag = null;
      this.timer = null;
      this.suppressClick = false;

      this.onVisibility = this.onVisibility.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onReduce = this.onReduce.bind(this);
      this.tick = this.tick.bind(this);

      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.mqReduce.addEventListener) this.mqReduce.addEventListener('change', this.onReduce);

      if (this.n > 1) {
        this.stage.addEventListener('pointerdown', this.onDown.bind(this));
        // A mouse drag that starts on a link would otherwise become the browser's native
        // drag-and-drop and cancel our pointer events.
        this.stage.addEventListener('dragstart', function (e) {
          e.preventDefault();
        });
        this.stage.addEventListener('click', this.onClickCapture.bind(this), true);
        this.addEventListener('keydown', this.onKey.bind(this));
        this.dots.forEach(
          function (dot) {
            dot.addEventListener(
              'click',
              function () {
                this.goTo(parseInt(dot.dataset.dot, 10));
              }.bind(this)
            );
          }.bind(this)
        );
      }

      // Theme Editor: selecting a review block shows it and stops autoplay so it can be edited.
      this.addEventListener(
        'shopify:block:select',
        function (e) {
          var idx = this.slides.indexOf(e.target.closest('[data-slide]'));
          if (idx > -1) {
            this.autoplayOn = false;
            this.clearTimer();
            this.goTo(idx);
          }
        }.bind(this)
      );

      this.render(true);
      this.updateDots();

      // Intro (eyebrow + heading) reveal on scroll: opacity + a rise, re-armed once it drops
      // back to barely visible so scrolling away and back plays it again. The review body
      // (quote + dots) is revealed by this exact same trigger, at the exact same moment — the
      // staggered, headline-first-then-reviews sequencing is done entirely in CSS via a
      // transition-delay on .rc__body (see assets/section-review-testimonial-carousel.css), not
      // by delaying anything here. Reduced motion shows both immediately, unanimated — same
      // convention as the rest of this theme.
      if (this.intro) {
        if (this.mqReduce.matches) {
          this.intro.classList.add('is-visible');
          if (this.body) this.body.classList.add('is-visible');
        } else if ('IntersectionObserver' in window) {
          this.introInView = false;
          this.introObserver = new IntersectionObserver(
            function (entries) {
              var en = entries[entries.length - 1];
              if (en.intersectionRatio >= 0.6 && !this.introInView) {
                this.introInView = true;
                this.intro.classList.add('is-visible');
                if (this.body) this.body.classList.add('is-visible');
              } else if (en.intersectionRatio <= 0.1 && this.introInView) {
                this.introInView = false;
                this.intro.classList.remove('is-visible');
                if (this.body) this.body.classList.remove('is-visible');
              }
            }.bind(this),
            { threshold: [0, 0.1, 0.6] }
          );
          this.introObserver.observe(this.intro);
        } else {
          this.intro.classList.add('is-visible');
          if (this.body) this.body.classList.add('is-visible');
        }
      }

      // Autoplay only actually runs while the section is meaningfully on screen.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          function (entries) {
            var en = entries[entries.length - 1];
            this.hold.offscreen = en.intersectionRatio < 0.4;
            this.updateTimer();
          }.bind(this),
          { threshold: [0, 0.4] }
        );
        this.io.observe(this);
      } else {
        this.hold.offscreen = false;
        this.updateTimer();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
      if (this.io) this.io.disconnect();
      if (this.introObserver) this.introObserver.disconnect();
      if (this.mqReduce && this.mqReduce.removeEventListener) {
        this.mqReduce.removeEventListener('change', this.onReduce);
      }
      this.clearTimer();
      this._ready = false;
    }

    /* ---------- autoplay ---------- */
    isRunning() {
      return this.autoplayOn && !this.hold.hidden && !this.hold.offscreen && !this.hold.dragging;
    }

    clearTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    updateTimer() {
      this.clearTimer();
      if (!this.isRunning()) return;
      var seconds = parseFloat(getComputedStyle(this).getPropertyValue('--rc-duration')) || 6;
      this.timer = window.setTimeout(this.tick, seconds * 1000);
    }

    tick() {
      if (!this.isRunning()) return;
      this.goTo((this.cur + 1) % this.n);
    }

    onVisibility() {
      this.hold.hidden = document.hidden;
      this.updateTimer();
    }

    onReduce() {
      if (!this.mqReduce.matches) return;
      this.autoplayOn = false;
      this.clearTimer();
      if (this.introObserver) {
        this.introObserver.disconnect();
        this.introObserver = null;
      }
      if (this.intro) this.intro.classList.add('is-visible');
      if (this.body) this.body.classList.add('is-visible');
    }

    /* ---------- positioning ---------- */
    // One review visible at a time: the shortest path around the loop.
    offsetFor(i) {
      var o = i - this.cur;
      if (this.loop) {
        o = ((o % this.n) + this.n) % this.n;
        if (o > this.n / 2) o -= this.n;
      }
      return o;
    }

    render(instant) {
      var self = this;
      var teleported = [];
      this.slides.forEach(function (slide, i) {
        var o = self.offsetFor(i);
        var jump = self.loop && !instant && Math.abs(o - self.prevO[i]) > self.n / 2;
        if (jump) {
          // Crossing the "back" of the loop: reposition without animating, then fade in.
          slide.classList.add('rc-tp', 'rc-hold');
          teleported.push(slide);
        }
        self.prevO[i] = o;
        slide.style.setProperty('--o', o);
        var active = o === 0;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      if (teleported.length) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            teleported.forEach(function (s) {
              s.classList.remove('rc-tp');
              s.classList.remove('rc-hold');
            });
          });
        });
      }
    }

    updateDots() {
      var cur = this.cur;
      this.dots.forEach(function (dot, i) {
        var on = i === cur;
        dot.classList.toggle('is-current', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(i) {
      i = ((i % this.n) + this.n) % this.n;
      if (i !== this.cur) {
        this.cur = i;
        this.render(false);
        this.updateDots();
      }
      this.updateTimer(); // a new selection always restarts the autoplay timer from 0
    }

    /* ---------- pointer / touch dragging ---------- */
    slotWidth() {
      return this.stage.getBoundingClientRect().width || 1;
    }

    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('button, a')) return;
      this.drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        dx: 0,
        active: false,
        slot: this.slotWidth(),
      };
      window.addEventListener('pointermove', this.onMove, { passive: true });
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    }

    onMove(e) {
      var d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      var dy = e.clientY - d.y;
      if (!d.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
          this.endTracking();
          return; // vertical scroll: leave it to the browser
        }
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        d.active = true;
        this.hold.dragging = true;
        this.classList.add('is-dragging');
        this.updateTimer();
        try {
          this.stage.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      // Without a loop (fewer than 3 reviews), resist dragging past the ends.
      if (!this.loop && ((this.cur === 0 && dx > 0) || (this.cur === this.n - 1 && dx < 0))) dx *= 0.3;
      d.dx = dx;
      this.style.setProperty('--rc-drag', dx + 'px');
      for (var i = 0; i < this.n; i++) {
        var pos = this.prevO[i] + dx / d.slot;
        this.slides[i].style.opacity = clamp(1 - Math.abs(pos), 0, 1);
      }
    }

    onUp(e) {
      var d = this.drag;
      if (!d || (e && e.pointerId !== d.id)) return;
      var wasActive = d.active;
      var dx = d.dx;
      this.endTracking();
      if (!wasActive) return;
      this.suppressClick = true;
      window.setTimeout(
        function () {
          this.suppressClick = false;
        }.bind(this),
        0
      );
      this.hold.dragging = false;
      this.classList.remove('is-dragging');
      var threshold = d.slot * 0.2;
      var next = this.cur;
      if (dx < -threshold) next = this.cur + 1;
      else if (dx > threshold) next = this.cur - 1;
      if (!this.loop) next = clamp(next, 0, this.n - 1);
      // Hand back to CSS: clear the live drag and inline opacity so the slide animates to its target.
      this.style.setProperty('--rc-drag', '0px');
      for (var i = 0; i < this.n; i++) this.slides[i].style.opacity = '';
      this.goTo(next);
    }

    endTracking() {
      this.drag = null;
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
    }

    onClickCapture(e) {
      // A drag must not also follow a link under the finger.
      if (this.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    onKey(e) {
      if (this.n < 2) return;
      if (e.key === 'ArrowRight') {
        this.goTo(this.cur + 1);
      } else if (e.key === 'ArrowLeft') {
        this.goTo(this.cur - 1);
      }
    }
  }

  window.customElements.define('review-testimonial-carousel', ReviewTestimonialCarousel);
})();
